import React, { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Building2, LineChart, Users, FileText, DollarSign, FileCheck, Video, CheckCircle2, ExternalLink, BarChart3, AlertCircle, Play, Link2,
  Mail, Linkedin, Globe, Home, MapPin, Calendar, Key, Shield, Lock, FileSearch, BarChart2, Calculator, TrendingUp, AlertTriangle,
  ClipboardList, Phone, MessageSquare, Download, Eye, Star, Clock, Percent, Building, PieChart, Target, Award, Camera, Map,
  Briefcase, FileSpreadsheet, UserCheck, Gavel, Scale, CreditCard, Banknote, Receipt, FileImage, Compass, Navigation
} from 'lucide-react';

// Enhanced mock data for a luxury real estate property with broker-specific features
const demoProperty = {
  name: 'The Skyline Residences',
  tagline: 'Prime Manhattan Investment Opportunity - Stabilized Asset with Strong NOI Growth',
  logo: 'SR',
  logoUrl: 'https://firebasestorage.googleapis.com/v0/b/decklink-9fa82.firebasestorage.app/o/penthouse.jpg?alt=media&token=b1117a1c-2163-4427-9e1b-7c9346cb5ed5',
  website: 'https://skyline-residences.com',
  location: '350 5th Avenue, New York, NY 10118',
  type: 'Class A Multifamily',
  listingId: 'MLS-2024-001247',
  listingStatus: 'Active - Exclusive',
  listDate: '2024-01-15',
  daysOnMarket: 45,
  
  // Market Analysis
  marketAnalysis: {
    submarket: 'Midtown Manhattan',
    pricePerSqFt: '$1,250',
    rentGrowth: '+4.8%',
    marketCap: '3.8% - 4.5%',
    comparableSales: [
      { address: '320 5th Ave', price: '$145M', date: '2023-11', capRate: '4.1%' },
      { address: '375 Park Ave', price: '$165M', date: '2023-09', capRate: '3.9%' },
      { address: '340 Madison Ave', price: '$128M', date: '2023-12', capRate: '4.3%' }
    ]
  },

  // Investment Highlights
  investmentHighlights: [
    'Prime Midtown Manhattan location with excellent transportation access',
    'Recently completed $8M capital improvement program',
    'Strong tenant credit profile - 85% investment grade tenants',
    'Below-market rents with significant upside potential',
    'Tax abatement in place through 2029 (421-a benefits)',
    'Professional management in place with 15+ year track record'
  ],

  // Key Investment Metrics
  metrics: {
    askingPrice: '$142,500,000',
    pricePerUnit: '$1,187,500',
    units: '120',
    pricePerSqFt: '$1,250',
    occupancy: '95.3%',
    capRate: '4.2%',
    grossYield: '5.8%',
    dscr: '1.35x',
    ltv: '65%',
    irr: '12.8%',
    cocReturn: '8.5%'
  },

  // Broker & Transaction Info
  brokerInfo: {
    listingBroker: {
      name: 'Sarah Anderson',
      license: 'NYS License #10401234567',
      company: 'Premier Manhattan Realty',
      phone: '+1 (212) 555-0147',
      email: 'sarah@premiermanhattan.com',
      certifications: ['CCIM', 'CIPS', 'CRE'],
      experience: '15+ years Manhattan commercial'
    },
    coListingBroker: {
      name: 'Michael Chen',
      license: 'NYS License #10401234568',
      company: 'Premier Manhattan Realty',
      phone: '+1 (212) 555-0148',
      email: 'michael@premiermanhattan.com'
    },
    commission: '3% total (1.5% each side)',
    exclusiveUntil: '2024-06-15',
    showingInstructions: 'By appointment only - 24hr notice required'
  },

  teamMembers: [
    {
      firstName: 'Sarah',
      lastName: 'Anderson',
      role: 'Licensed Broker - Lead Agent',
      bio: 'Licensed Real Estate Broker with 15+ years specializing in Manhattan luxury investment properties. CCIM certified with $2B+ in closed transactions.',
      initials: 'SA',
      email: 'sarah@premiermanhattan.com',
      phone: '+1 (212) 555-0147',
      linkedin: 'https://www.linkedin.com/in/sarah-anderson',
      website: 'https://premiermanhattan.com/sarah-anderson',
      license: 'NYS #10401234567',
      certifications: ['CCIM', 'CIPS', 'CRE']
    },
    {
      firstName: 'Michael',
      lastName: 'Chen',
      role: 'Investment Specialist',
      bio: 'Licensed Broker and former Goldman Sachs real estate investment specialist. Expert in REIT structuring, 1031 exchanges, and institutional transactions.',
      initials: 'MC',
      email: 'michael@premiermanhattan.com',
      phone: '+1 (212) 555-0148',
      linkedin: 'https://www.linkedin.com/in/michael-chen',
      website: 'https://premiermanhattan.com/michael-chen',
      license: 'NYS #10401234568',
      certifications: ['CFA', 'CCIM']
    },
    {
      firstName: 'Emma',
      lastName: 'Rodriguez',
      role: 'Transaction Coordinator',
      bio: 'Licensed Associate Broker specializing in due diligence coordination and closing management. Expert in complex commercial transactions.',
      initials: 'ER',
      email: 'emma@premiermanhattan.com',
      phone: '+1 (212) 555-0149',
      linkedin: 'https://www.linkedin.com/in/emma-rodriguez',
      website: 'https://premiermanhattan.com/emma-rodriguez',
      license: 'NYS #10401234569'
    }
  ],

  propertyDetails: {
    yearBuilt: '2022',
    totalFloors: '45',
    totalUnits: '120',
    totalSqFt: '114,000',
    lotSize: '15,000 sq ft',
    zoning: 'R10A',
    taxes: '$1,250,000/year',
    taxAbatement: '421-a through 2029',
    unitMix: {
      'Studio': { count: 20, avgRent: '$4,200', sqft: '450-550' },
      '1BR': { count: 45, avgRent: '$6,800', sqft: '650-750' },
      '2BR': { count: 40, avgRent: '$12,500', sqft: '950-1100' },
      '3BR': { count: 15, avgRent: '$18,000', sqft: '1200-1400' }
    },
    amenities: [
      'Rooftop Pool & Sundeck',
      'State-of-the-Art Fitness Center',
      '24/7 Concierge Service',
      'Attended Parking Garage (80 spaces)',
      'Smart Home Technology Package',
      'Residents Lounge & Co-working Space',
      'Children\'s Playroom',
      'Bike Storage & Repair Station'
    ],
    features: [
      'Floor-to-ceiling windows with city views',
      'Private balconies in 90% of units',
      'High-end Bosch appliance packages',
      'Custom Italian cabinetry',
      'Carrara marble bathrooms',
      'Hardwood flooring throughout',
      'In-unit washer/dryer',
      'Smart thermostats and lighting'
    ]
  },

  // Enhanced Financial Information
  financials: {
    askingPrice: 142500000,
    grossIncome: 20400000,
    operatingExpenses: 8250000,
    noi: 12150000,
    capRate: '4.2%',
    occupancy: '95.3%',
    avgRentPsf: '$178',
    expenseRatio: '40.4%',
    debt: {
      currentLoan: 92625000,
      rate: '4.75%',
      term: '10 years',
      amortization: '25 years',
      maturity: '2034-03-15'
    },
    taxes: 1250000,
    insurance: 485000,
    utilities: 325000,
    maintenance: 890000,
    management: 612000,
    reserves: 145000,
    projections: {
      year1: { income: 21012000, noi: 12607200, growth: '3.5%' },
      year2: { income: 21642360, noi: 12983416, growth: '3.0%' },
      year3: { income: 22291631, noi: 13374979, growth: '3.0%' },
      year4: { income: 22960380, noi: 13782228, growth: '3.0%' },
      year5: { income: 23649191, noi: 14205695, growth: '3.0%' }
    }
  },

  // Due Diligence Package
  documents: [
    { name: 'Offering Memorandum', uploaded: true, required: true, fileName: 'OM_Skyline_Residences.pdf', lastUpdated: '2024-01-15', restricted: true },
    { name: 'Property Appraisal (MAI)', uploaded: true, required: true, fileName: 'appraisal_MAI_certified.pdf', lastUpdated: '2023-12-01', restricted: true },
    { name: 'Phase I Environmental Report', uploaded: true, required: true, fileName: 'environmental_phase1.pdf', lastUpdated: '2023-11-15', restricted: false },
    { name: 'Engineering Report', uploaded: true, required: true, fileName: 'engineering_inspection.pdf', lastUpdated: '2023-11-20', restricted: false },
    { name: 'Rent Roll (Current)', uploaded: true, required: true, fileName: 'rent_roll_current.xlsx', lastUpdated: '2024-01-01', restricted: true },
    { name: 'Trailing 12 Months P&L', uploaded: true, required: true, fileName: 'financials_TTM.pdf', lastUpdated: '2023-12-31', restricted: true },
    { name: 'Tax Returns (3 Years)', uploaded: true, required: true, fileName: 'tax_returns_2021-2023.pdf', lastUpdated: '2023-12-01', restricted: true },
    { name: 'Certificate of Occupancy', uploaded: true, required: true, fileName: 'CO_certificate.pdf', lastUpdated: '2022-05-15', restricted: false },
    { name: 'Building Permits & Violations', uploaded: true, required: true, fileName: 'permits_violations.pdf', lastUpdated: '2024-01-01', restricted: false },
    { name: 'Insurance Policies', uploaded: true, required: true, fileName: 'insurance_policies.pdf', lastUpdated: '2024-01-01', restricted: false },
    { name: 'Management Agreement', uploaded: true, required: true, fileName: 'management_agreement.pdf', lastUpdated: '2023-06-01', restricted: true },
    { name: 'Capital Improvements Summary', uploaded: true, required: true, fileName: 'capex_summary.pdf', lastUpdated: '2023-12-01', restricted: false },
    { name: 'Tenant Leases (Sample)', uploaded: true, required: false, fileName: 'sample_leases.pdf', lastUpdated: '2023-12-01', restricted: true },
    { name: 'Utility Bills (12 months)', uploaded: true, required: false, fileName: 'utility_bills.pdf', lastUpdated: '2023-12-31', restricted: false },
    { name: 'Property Survey', uploaded: true, required: false, fileName: 'property_survey.pdf', lastUpdated: '2022-03-01', restricted: false }
  ],

  // Virtual Tour & Media
  virtualTour: {
    videoUrl: 'https://www.youtube.com/watch?v=9KYHsjTOYfU',
    videoName: 'Skyline_Residences_Professional_Tour.mp4',
    lastUpdated: '2024-01-01',
    tourLink: 'https://skyline-residences.com/virtual-tour',
    features: [
      'Professional Videography Tour',
      'Drone Footage of Building & Neighborhood',
      'Model Unit Walkthroughs',
      'Amenity Spaces Showcase',
      'Investment Potential Analysis',
      '3D Matterport Virtual Tour'
    ],
    additionalMedia: [
      { type: 'Floor Plans', count: 12, lastUpdated: '2024-01-01' },
      { type: 'Professional Photos', count: 85, lastUpdated: '2024-01-01' },
      { type: 'Neighborhood Map', count: 1, lastUpdated: '2024-01-01' },
      { type: 'Investment Presentation', count: 1, lastUpdated: '2024-01-01' }
    ]
  },

  // Market Comparables
  comparables: [
    { address: '320 5th Avenue', price: '$145M', units: 115, ppu: '$1.26M', capRate: '4.1%', date: '2023-11' },
    { address: '375 Park Avenue', price: '$165M', units: 125, ppu: '$1.32M', capRate: '3.9%', date: '2023-09' },
    { address: '340 Madison Avenue', price: '$128M', units: 108, ppu: '$1.19M', capRate: '4.3%', date: '2023-12' }
  ]
};

const EnhancedRealEstateDealRoom = () => {
  const teamRef = useRef(null);
  const docsRef = useRef(null);
  const compsRef = useRef(null);
  const [activeTeam, setActiveTeam] = useState(0);
  const [activeDoc, setActiveDoc] = useState(0);
  const [activeComp, setActiveComp] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showRestrictedDocs, setShowRestrictedDocs] = useState(false);

  const handleScroll = (ref, setActive, items) => {
    if (!ref.current) return;
    const scrollLeft = ref.current.scrollLeft;
    const cardWidth = ref.current.offsetWidth * 0.8;
    const idx = Math.round(scrollLeft / (cardWidth * 0.95));
    setActive(Math.max(0, Math.min(items - 1, idx)));
  };

  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div className="flex-1 max-w-5xl mx-auto px-2 sm:px-6 py-8 sm:py-12 w-full">
        
        {/* Property Header & Key Metrics */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center mb-10 sm:mb-14 bg-white/95 backdrop-blur-lg rounded-3xl p-4 sm:p-10 shadow-2xl">
          {/* {demoProperty.logoUrl ? (
            <div className="w-48 h-48 sm:w-64 sm:h-64 mx-auto mb-8 sm:mb-12">
              <img 
                src={demoProperty.logoUrl} 
                alt={`${demoProperty.name} logo`}
                className="w-[150%] h-[150%] object-contain rounded-lg"
              />
            </div>
          ) : (
            <div className="w-48 h-48 sm:w-64 sm:h-64 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg mx-auto mb-8 sm:mb-12 flex items-center justify-center text-white text-2xl sm:text-3xl font-bold">
              {demoProperty.logo}
            </div>
          )} */}
          <h1 className="text-3xl sm:text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2 sm:mb-4">
            {demoProperty.name}
          </h1>
          <p className="text-base sm:text-xl text-gray-600 mb-4 sm:mb-6 max-w-3xl mx-auto">
            {demoProperty.tagline}
          </p>
          
          {/* Listing Information */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-6 text-xs sm:text-sm">
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-50 rounded-xl text-green-700">
              <CheckCircle2 className="w-4 h-4" /> {demoProperty.listingStatus}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-xl text-blue-700">
              <FileText className="w-4 h-4" /> MLS #{demoProperty.listingId}
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-orange-50 rounded-xl text-orange-700">
              <Clock className="w-4 h-4" /> {demoProperty.daysOnMarket} Days on Market
            </span>
            <span className="inline-flex items-center gap-2 px-3 py-1 bg-purple-50 rounded-xl text-purple-700">
              <MapPin className="w-4 h-4" /> {demoProperty.location}
            </span>
          </div>

          {/* Key Investment Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-6 mb-6">
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-green-500 to-emerald-500 bg-clip-text text-transparent">
                {formatCurrency(demoProperty.financials.askingPrice)}
              </div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1">Asking Price</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                {demoProperty.metrics.capRate}
              </div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1">Cap Rate</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                {demoProperty.metrics.occupancy}
              </div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1">Occupancy</div>
            </div>
            <div className="bg-white/70 rounded-2xl p-3 sm:p-6 text-center">
              <div className="text-lg sm:text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
                {demoProperty.metrics.irr}
              </div>
              <div className="text-gray-600 text-xs sm:text-sm mt-1">Projected IRR</div>
            </div>
          </div>

          {/* Additional Key Metrics */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
              <div className="font-bold text-gray-900">{demoProperty.metrics.pricePerUnit}</div>
              <div className="text-gray-600">Price/Unit</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
              <div className="font-bold text-gray-900">{demoProperty.metrics.grossYield}</div>
              <div className="text-gray-600">Gross Yield</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
              <div className="font-bold text-gray-900">{demoProperty.metrics.dscr}</div>
              <div className="text-gray-600">DSCR</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
              <div className="font-bold text-gray-900">{demoProperty.metrics.cocReturn}</div>
              <div className="text-gray-600">Cash-on-Cash</div>
            </div>
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 text-center">
              <div className="font-bold text-gray-900">{demoProperty.metrics.ltv}</div>
              <div className="text-gray-600">Target LTV</div>
            </div>
          </div>
        </motion.div>
           {/* Virtual Tour & Media */}
           <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Video className="w-6 h-6 text-purple-500" /> Virtual Tour & Media
          </h2>
          
          {demoProperty.virtualTour.videoUrl && (
            <div className="aspect-video bg-black rounded-2xl overflow-hidden mb-6 relative group cursor-pointer">
              {!isVideoPlaying ? (
                <div 
                  onClick={() => setIsVideoPlaying(true)}
                  className="relative w-full h-full"
                >
                  <img 
                    src={`https://img.youtube.com/vi/${getYouTubeVideoId(demoProperty.virtualTour.videoUrl)}/maxresdefault.jpg`}
                    alt="Virtual tour thumbnail"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <div className="w-20 h-20 bg-red-600 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                  </div>
                </div>
              ) : (
                <iframe
                  width="100%"
                  height="100%"
                  src={`https://www.youtube.com/embed/${getYouTubeVideoId(demoProperty.virtualTour.videoUrl)}?autoplay=1`}
                  title="Virtual Tour"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              )}
            </div>
          )}
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Tour Features</h3>
              <div className="space-y-2">
                {demoProperty.virtualTour.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span className="text-sm">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Media</h3>
              <div className="space-y-3">
                {demoProperty.virtualTour.additionalMedia.map((media, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-3">
                      <FileImage className="w-5 h-5 text-purple-500" />
                      <div>
                        <div className="font-medium text-gray-900">{media.type}</div>
                        <div className="text-xs text-gray-500">{media.count} files</div>
                      </div>
                    </div>
                    <button className="text-blue-600 hover:text-blue-800 text-sm">View</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          {demoProperty.virtualTour.tourLink && (
            <div className="text-center">
              <a 
                href={demoProperty.virtualTour.tourLink} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
              >
                <Navigation className="w-5 h-5" /> Take Interactive 3D Tour
              </a>
            </div>
          )}
        </motion.div>

        {/* Investment Highlights */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Target className="w-6 h-6 text-green-500" /> Investment Highlights
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {demoProperty.investmentHighlights.map((highlight, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-green-50 rounded-xl">
                <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{highlight}</span>
              </div>
            ))}
          </div>
        </motion.div>

        

        {/* Property Details & Unit Mix */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-500" /> Property Details & Unit Mix
          </h2>
          
          <div className="relative -mx-6 sm:mx-0">
            <div className="flex overflow-x-auto gap-6 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-0 sm:grid sm:grid-cols-2 lg:grid-cols-2 sm:gap-8">
              {/* Building Specs */}
              <div className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0" style={{ minWidth: '320px', width: '320px' }}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Building Specifications</h3>
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Year Built</span>
                    <span className="font-medium">{demoProperty.propertyDetails.yearBuilt}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Total Floors</span>
                    <span className="font-medium">{demoProperty.propertyDetails.totalFloors}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Total Square Feet</span>
                    <span className="font-medium">{demoProperty.propertyDetails.totalSqFt.toLocaleString()} SF</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Lot Size</span>
                    <span className="font-medium">{demoProperty.propertyDetails.lotSize}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Zoning</span>
                    <span className="font-medium">{demoProperty.propertyDetails.zoning}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Annual Taxes</span>
                    <span className="font-medium">{formatCurrency(demoProperty.financials.taxes)}</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-gray-600">Tax Abatement</span>
                    <span className="font-medium text-green-600">{demoProperty.propertyDetails.taxAbatement}</span>
                  </div>
                </div>
              </div>

              {/* Unit Mix */}
              <div className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0" style={{ minWidth: '320px', width: '320px' }}>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Unit Mix & Rent Roll</h3>
                <div className="space-y-3">
                  {Object.entries(demoProperty.propertyDetails.unitMix).map(([type, data]) => (
                    <div key={type} className="bg-gray-50 rounded-xl p-4">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-medium text-gray-900">{type}</span>
                        <span className="text-sm text-gray-600">{data.count} units</span>
                      </div>
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Avg Rent: </span>
                          <span className="font-medium">{data.avgRent}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Size: </span>
                          <span className="font-medium">{data.sqft} SF</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Amenities Grid */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Premium Amenities</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {demoProperty.propertyDetails.amenities.map((amenity, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                  <Star className="w-4 h-4 text-blue-500" />
                  <span className="text-gray-700 text-sm">{amenity}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Financial Performance */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-500" /> Financial Performance & Projections
          </h2>
          
          {/* Current Performance */}
          <div className="relative -mx-6 sm:mx-0">
            <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-0 sm:grid sm:grid-cols-4 sm:gap-4">
              <div className="bg-green-50 rounded-xl p-4 text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                <div className="text-xl font-bold text-green-900">{formatCurrency(demoProperty.financials.grossIncome)}</div>
                <div className="text-xs text-green-700">Gross Income</div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                <div className="text-xl font-bold text-blue-900">{formatCurrency(demoProperty.financials.noi)}</div>
                <div className="text-xs text-blue-700">Net Operating Income</div>
              </div>
              <div className="bg-purple-50 rounded-xl p-4 text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                <div className="text-xl font-bold text-purple-900">{demoProperty.financials.expenseRatio}</div>
                <div className="text-xs text-purple-700">Expense Ratio</div>
              </div>
              <div className="bg-orange-50 rounded-xl p-4 text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                <div className="text-xl font-bold text-orange-900">{demoProperty.financials.avgRentPsf}</div>
                <div className="text-xs text-orange-700">Avg Rent PSF</div>
              </div>
            </div>
          </div>

          {/* Operating Expenses Breakdown */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Operating Expenses Breakdown</h3>
            <div className="relative -mx-6 sm:mx-0">
              <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-0 sm:grid sm:grid-cols-3 sm:gap-4">
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Receipt className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-gray-900">Property Taxes</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.taxes)}</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <span className="font-medium text-gray-900">Insurance</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.insurance)}</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Building className="w-5 h-5 text-purple-500" />
                    <span className="font-medium text-gray-900">Maintenance</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.maintenance)}</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="w-5 h-5 text-green-500" />
                    <span className="font-medium text-gray-900">Management</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.management)}</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <BarChart3 className="w-5 h-5 text-yellow-500" />
                    <span className="font-medium text-gray-900">Utilities</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.utilities)}</div>
                </div>
                <div className="bg-white rounded-xl p-4 border border-gray-200 snap-center flex-shrink-0" style={{ minWidth: '280px', width: '280px' }}>
                  <div className="flex items-center gap-3 mb-2">
                    <PieChart className="w-5 h-5 text-indigo-500" />
                    <span className="font-medium text-gray-900">Reserves</span>
                  </div>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.reserves)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Debt Information */}
          <div className="mt-8 p-6 bg-gray-50 rounded-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-500" /> Current Financing
            </h3>
            <div className="relative -mx-6 sm:mx-0">
              <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-0 sm:grid sm:grid-cols-4 sm:gap-4">
                <div className="text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                  <div className="text-lg font-bold text-gray-900">{formatCurrency(demoProperty.financials.debt.currentLoan)}</div>
                  <div className="text-sm text-gray-600">Current Loan</div>
                </div>
                <div className="text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                  <div className="text-lg font-bold text-gray-900">{demoProperty.financials.debt.rate}</div>
                  <div className="text-sm text-gray-600">Interest Rate</div>
                </div>
                <div className="text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                  <div className="text-lg font-bold text-gray-900">{demoProperty.financials.debt.term}</div>
                  <div className="text-sm text-gray-600">Term</div>
                </div>
                <div className="text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                  <div className="text-lg font-bold text-gray-900">{demoProperty.financials.debt.maturity}</div>
                  <div className="text-sm text-gray-600">Maturity Date</div>
                </div>
              </div>
            </div>
          </div>

          {/* 5-Year Projections */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-500" /> 5-Year Financial Projections
            </h3>
            <div className="relative -mx-6 sm:mx-0">
              <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-0 sm:grid sm:grid-cols-5 sm:gap-4">
                {Object.entries(demoProperty.financials.projections).map(([year, data], index) => (
                  <div key={year} className="bg-gradient-to-b from-blue-50 to-blue-100 rounded-xl p-4 text-center snap-center flex-shrink-0" style={{ minWidth: '200px', width: '200px' }}>
                    <div className="text-sm font-medium text-blue-800 mb-2">Year {index + 1}</div>
                    <div className="text-lg font-bold text-blue-900 mb-1">{formatCurrency(data.income)}</div>
                    <div className="text-xs text-blue-700 mb-2">Gross Income</div>
                    <div className="text-md font-bold text-green-800 mb-1">{formatCurrency(data.noi)}</div>
                    <div className="text-xs text-green-700 mb-2">NOI</div>
                    <div className="text-xs bg-blue-200 rounded px-2 py-1 text-blue-800">+{data.growth} Growth</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Market Comparables */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-purple-500" /> Market Comparables
          </h2>
          <div className="relative">
            <div
              ref={compsRef}
              className="flex overflow-x-auto gap-6 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              onScroll={() => handleScroll(compsRef, setActiveComp, demoProperty.comparables.length)}
            >
              {demoProperty.comparables.map((comp, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0 border border-gray-200"
                  style={{ minWidth: '320px', width: '320px' }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <Building2 className="w-8 h-8 text-purple-500" />
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">{comp.address}</h3>
                      <p className="text-sm text-gray-600">Sold {comp.date}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sale Price</span>
                      <span className="font-bold text-green-600">{comp.price}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Units</span>
                      <span className="font-medium">{comp.units}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Price/Unit</span>
                      <span className="font-medium">{comp.ppu}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cap Rate</span>
                      <span className="font-bold text-blue-600">{comp.capRate}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 gap-2">
              {demoProperty.comparables.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveComp(idx);
                    if (compsRef.current) {
                      const cardWidth = 320 + 24;
                      compsRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                    }
                  }}
                  className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeComp === idx ? 'bg-purple-500 scale-125' : 'bg-gray-300'}`}
                  aria-label={`Show comparable ${idx + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Broker Team */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-500" /> Licensed Broker Team
          </h2>
          
          {/* Listing Broker Info */}
          <div className="mb-8 p-6 bg-blue-50 rounded-2xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-blue-500" /> Listing Information
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Commission: </span>
                <span className="font-medium">{demoProperty.brokerInfo.commission}</span>
              </div>
              <div>
                <span className="text-gray-600">Exclusive Until: </span>
                <span className="font-medium">{demoProperty.brokerInfo.exclusiveUntil}</span>
              </div>
              <div className="sm:col-span-2">
                <span className="text-gray-600">Showing Instructions: </span>
                <span className="font-medium">{demoProperty.brokerInfo.showingInstructions}</span>
              </div>
            </div>
          </div>

          {/* Team Members */}
          <div className="relative -mx-6 sm:-mx-10">
            <div
              ref={teamRef}
              className="flex overflow-x-auto gap-6 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden px-6 sm:px-10"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              onScroll={() => handleScroll(teamRef, setActiveTeam, demoProperty.teamMembers.length)}
            >
              {demoProperty.teamMembers.map((member, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl p-6 shadow-lg snap-center flex-shrink-0 border border-gray-200"
                  style={{ minWidth: '320px', width: '320px' }}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-400 to-purple-400 rounded-xl flex items-center justify-center text-white text-lg font-bold">
                      {member.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-gray-900">{member.firstName} {member.lastName}</h3>
                      <p className="text-sm text-gray-600">{member.role}</p>
                      <p className="text-xs text-gray-500">License: {member.license}</p>
                    </div>
                  </div>
                  
                  {member.certifications && (
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1">
                        {member.certifications.map((cert, idx) => (
                          <span key={idx} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-gray-600 mb-4 leading-relaxed">{member.bio}</p>
                  
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${member.phone}`} className="text-blue-600 hover:underline">{member.phone}</a>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${member.email}`} className="text-blue-600 hover:underline">{member.email}</a>
                    </div>
                    <div className="flex gap-2 mt-3">
                      {member.linkedin && (
                        <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                          <Linkedin className="w-4 h-4" />
                        </a>
                      )}
                      {member.website && (
                        <a href={member.website} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-500 transition-colors">
                          <Globe className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex justify-center mt-4 gap-2">
              {demoProperty.teamMembers.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveTeam(idx);
                    if (teamRef.current) {
                      const cardWidth = 320 + 24;
                      teamRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                    }
                  }}
                  className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${activeTeam === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'}`}
                  aria-label={`Show team member ${idx + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Due Diligence Documents */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <FileCheck className="w-6 h-6 text-blue-500" /> Due Diligence Package
          </h2>
          
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">Complete documentation package for qualified investors</p>
            <button
              onClick={() => setShowRestrictedDocs(!showRestrictedDocs)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm"
            >
              <Lock className="w-4 h-4" />
              {showRestrictedDocs ? 'Hide' : 'Show'} Restricted Docs
            </button>
          </div>

          <div className="relative">
            <div
              ref={docsRef}
              className="flex overflow-x-auto gap-4 snap-x snap-mandatory [&::-webkit-scrollbar]:hidden"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
              onScroll={() => handleScroll(docsRef, setActiveDoc, demoProperty.documents.length)}
            >
              {demoProperty.documents
                .filter(doc => showRestrictedDocs || !doc.restricted)
                .map((doc, idx) => (
                <div
                  key={idx}
                  className={`flex flex-col gap-3 p-4 rounded-xl border snap-center flex-shrink-0 ${
                    doc.uploaded 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}
                  style={{ minWidth: '280px', maxWidth: '320px' }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {doc.uploaded ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                      {doc.restricted && (
                        <Lock className="w-4 h-4 text-red-500 flex-shrink-0" />
                      )}
                    </div>
                    {doc.required && (
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Required</span>
                    )}
                  </div>
                  
                  <div>
                    <div className="font-medium text-gray-900 mb-1">{doc.name}</div>
                    <div className="text-xs text-gray-500 mb-2">
                      {doc.uploaded ? `File: ${doc.fileName}` : 'Not uploaded'}
                    </div>
                    {doc.lastUpdated && (
                      <div className="text-xs text-gray-400">
                        Updated: {new Date(doc.lastUpdated).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                  
                  {doc.uploaded && !doc.restricted && (
                    <div className="flex gap-2 mt-2">
                      <button className="flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200 transition-colors">
                        <Eye className="w-3 h-3" /> View
                      </button>
                      <button className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded text-xs hover:bg-gray-200 transition-colors">
                        <Download className="w-3 h-3" /> Download
                      </button>
                    </div>
                  )}
                  
                  {doc.restricted && (
                    <div className="text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                      NDA Required
                    </div>
                  )}
                </div>
              ))}
            </div>
            
            <div className="flex justify-center mt-4 gap-2">
              {demoProperty.documents
                .filter(doc => showRestrictedDocs || !doc.restricted)
                .map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveDoc(idx);
                    if (docsRef.current) {
                      const cardWidth = 300;
                      docsRef.current.scrollTo({ left: idx * cardWidth, behavior: 'smooth' });
                    }
                  }}
                  className={`w-2 h-2 rounded-full inline-block transition-all duration-200 focus:outline-none ${
                    activeDoc === idx ? 'bg-blue-500 scale-125' : 'bg-gray-300'
                  }`}
                  aria-label={`Show document ${idx + 1}`}
                ></button>
              ))}
            </div>
          </div>
        </motion.div>

     

        {/* Broker Tools & Actions */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-indigo-500" /> Broker Tools & Resources
          </h2>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Investment Calculators */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calculator className="w-8 h-8 text-blue-600" />
                <h3 className="text-lg font-semibold text-gray-900">Investment Calculators</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-blue-50 transition-colors text-sm">
                  ROI & Cash Flow Calculator
                </button>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-blue-50 transition-colors text-sm">
                  1031 Exchange Analyzer
                </button>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-blue-50 transition-colors text-sm">
                  Financing Scenarios
                </button>
              </div>
            </div>

            {/* Market Reports */}
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <FileSpreadsheet className="w-8 h-8 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Market Reports</h3>
              </div>
              <div className="space-y-2">
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-green-50 transition-colors text-sm">
                  Neighborhood Analysis
                </button>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-green-50 transition-colors text-sm">
                  Comparable Sales Report
                </button>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-green-50 transition-colors text-sm">
                  Market Trends Dashboard
                </button>
              </div>
            </div>

            {/* Scheduling & Communication */}
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <Calendar className="w-8 h-8 text-purple-600" />
                <h3 className="text-lg font-semibold text-gray-900">Schedule & Connect</h3>
              </div>
              <div className="space-y-2">
                <a 
                  href="https://calendly.com/sarah-anderson-broker" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-purple-50 transition-colors text-sm"
                >
                  Schedule Property Tour
                </a>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-purple-50 transition-colors text-sm">
                  Request NDA Package
                </button>
                <button className="w-full text-left px-3 py-2 bg-white rounded-lg hover:bg-purple-50 transition-colors text-sm">
                  Join Interest List
                </button>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contact & Next Steps */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }} className="bg-white/95 backdrop-blur-lg rounded-3xl p-6 sm:p-10 shadow-2xl text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Ready to Move Forward?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            This exclusive Manhattan investment opportunity won't last long. Contact our licensed broker team today to schedule a private tour, 
            access the complete financial package, or discuss financing options.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="p-4 bg-blue-50 rounded-xl">
              <Phone className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Call Direct</h3>
              <a href={`tel:${demoProperty.teamMembers[0].phone}`} className="text-blue-600 hover:underline text-sm">
                {demoProperty.teamMembers[0].phone}
              </a>
            </div>
            
            <div className="p-4 bg-green-50 rounded-xl">
              <Mail className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Email Inquiry</h3>
              <a href={`mailto:${demoProperty.teamMembers[0].email}?subject=Investment Inquiry - ${demoProperty.name}`} className="text-green-600 hover:underline text-sm">
                Send Message
              </a>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-xl">
              <Calendar className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <h3 className="font-semibold text-gray-900 mb-2">Schedule Tour</h3>
              <a href="https://calendly.com/sarah-anderson-broker" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline text-sm">
                Book Appointment
              </a>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-4">
            <a 
              href={`mailto:${demoProperty.teamMembers[0].email}?subject=NDA Request - ${demoProperty.name}&body=I am interested in accessing the complete due diligence package for ${demoProperty.name}. Please send the NDA for execution.`} 
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              <FileCheck className="w-5 h-5" /> Request NDA Package
            </a>
            <a 
              href={`mailto:${demoProperty.teamMembers[0].email}?subject=Financing Discussion - ${demoProperty.name}&body=I would like to discuss financing options for ${demoProperty.name}. Please schedule a call to review terms and lender relationships.`} 
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-xl hover:shadow-lg transition-all"
            >
              <CreditCard className="w-5 h-5" /> Discuss Financing
            </a>
          </div>
        </motion.div>

        {/* Legal Disclaimer */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="bg-gray-50 rounded-2xl p-6 mb-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Scale className="w-5 h-5 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Important Disclosure</h3>
          </div>
          <p className="text-xs text-gray-600 leading-relaxed max-w-4xl mx-auto">
            This information is confidential and proprietary. All financial projections are estimates based on current market conditions and are not guaranteed. 
            This is not an offer to sell securities. Licensed Real Estate Broker. Equal Housing Opportunity. All information deemed reliable but not guaranteed. 
            Prospective buyers should conduct their own due diligence and consult with qualified professionals before making investment decisions.
          </p>
        </motion.div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 1 }} 
          className="text-center py-8"
        >
          <div className="flex flex-col items-center gap-4">
            <a
              href="/signup"
              className="px-6 py-2.5 rounded-full bg-black text-white text-sm font-medium hover:bg-gray-800 transition-colors"
            >
              Create Your Broker Deal Room
            </a>
            
            <div className="text-xs text-gray-400 mb-2">
              Professional real estate transaction platform for licensed brokers
            </div>
            
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <div className="w-1.5 h-1.5 rounded-full bg-gray-300"></div>
              <span>
                Powered by{' '}
                <a 
                  href="https://decklink.co" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-gray-600 transition-colors"
                >
                  decklink.co
                </a>
              </span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default EnhancedRealEstateDealRoom;